import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", description: "", preferred_time: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus("sent");
      toast.success("Received. We'll call you at your preferred time.");
      setForm({ name: "", phone: "", description: "", preferred_time: "" });
      setTimeout(() => setStatus("idle"), 3500);
    } catch {
      setStatus("idle");
      toast.error("Something went wrong. Please call us directly.");
    }
  };

  const field = "w-full bg-transparent border-b border-white/15 focus:border-[var(--gold)] outline-none py-3 text-white placeholder:text-white/30 transition-colors";

  return (
    <section id="contact" className="relative py-24 lg:py-36 border-t border-white/5" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.4em] uppercase text-[var(--gold)] mb-5">Get In Touch</p>
          <h2 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl text-white leading-none mb-10">
            Contact <span className="gold-fill">Now</span>
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <a href="tel:+18179321473" data-testid="contact-phone" className="btn-gold rounded-full px-9 py-4 text-base tracking-[0.12em] uppercase font-medium flex items-center gap-3">
              <Phone size={18} /> 817 932 1473
            </a>
            <a href="mailto:dusty@crownfw.com" data-testid="contact-email" className="btn-ghost rounded-full px-9 py-4 text-base tracking-[0.06em] flex items-center gap-3">
              <Mail size={18} /> dusty@crownfw.com
            </a>
          </div>
        </div>

        {/* Callback form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="glass rounded-2xl p-8 sm:p-12 max-w-2xl mx-auto"
        >
          <h3 className="font-display text-2xl sm:text-3xl text-white mb-2 text-center">Or have us contact you</h3>
          <p className="text-white/50 text-sm text-center mb-10">Leave your details and we'll reach out at the time that works best for you.</p>

          <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-white/40">Name</label>
                <input value={form.name} onChange={set("name")} placeholder="Your name" className={field} data-testid="input-name" />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-white/40">Phone Number</label>
                <input value={form.phone} onChange={set("phone")} placeholder="(817) 000 0000" className={field} data-testid="input-phone" />
              </div>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-white/40">Best Time to Call <span className="text-white/25 normal-case tracking-normal">(optional)</span></label>
              <input value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g. Weekdays after 5pm, or Saturday morning" className={field} data-testid="input-preferred-time" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-white/40">Anything you'd like to add <span className="text-white/25 normal-case tracking-normal">(optional)</span></label>
              <textarea value={form.description} onChange={set("description")} rows={3} placeholder="Tell us a little about what you need..." className={`${field} resize-none`} data-testid="input-description" />
            </div>

            <button
              type="submit"
              disabled={status !== "idle"}
              data-testid="contact-submit-btn"
              className="btn-gold rounded-full px-10 py-4 text-sm tracking-[0.2em] uppercase font-medium flex items-center gap-3 disabled:opacity-70 mx-auto"
            >
              {status === "sending" && <Loader2 size={16} className="animate-spin" />}
              {status === "sent" && <Check size={16} />}
              {status === "idle" ? "Request a Call" : status === "sending" ? "Sending" : "Received"}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
